import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSpring, animated } from 'react-spring';
import useComponentSize from '@rehooks/component-size';
import dt from '../../../shared/theme';

const dftH = 48;
const Form = styled(animated.form)`
  background-color: white;
  border: 1px solid #d7dfe7;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: ${dftH}px;
  box-sizing: content-box;
  overflow: hidden;

  &&&& {
    width: calc(100% - 2px);
  }

  > textarea {
    flex-shrink: 0;
    padding: 12px ${props => (dt(props).theme.isMobile ? 16 : 24)}px;
    border: none;
    resize: none;
    height: 120px;
  }

  > section {
    flex-shrink: 0;
    border-top: 1px solid #d7dfe7;
    box-sizing: content-box;
    width: calc(100% - 2px);
    height: 32px;
    display: flex;
    padding: 8px 0;
    align-items: center;
    justify-content: flex-end;
  }
`;

const Button = styled.button`
  border: none;
  border-radius: 4px;
  height: 32px;
  width: 92px;
  text-align: center;
  font: ${props => dt(props).theme.fontWeightHeavy} 14px/32px ${props => dt(props).theme.fontFamilyBody};
  text-transform: uppercase;

  &.no-focus-outline {
    outline: none;
  }

  &[name="cancel"] {
    background-color: transparent;
    color: ${props => dt(props).theme.bodyColor};
    margin-right: 8px;
  }

  &[name="button"] {
    margin-right: 16px;
    background-color: #3d4a63;
    color: white;
    padding: 0;
  }

  &[disabled] {
    opacity: 0.4;
  }
`;

export default function CommentForm({ wireSlug }) {
  React.useEffect(() => {
    const buttons = document.querySelectorAll('form section button');
    buttons.forEach(i => {
      i.addEventListener('keyup', e => {
        if (e.key === 'Tab') {
          i.classList.remove('no-focus-outline');
        }
      });
    });
  });

  const [value, setValue] = React.useState('');
  const [focused, setFocused] = React.useState(false);
  const taRef = React.useRef();
  const sRef = React.useRef();
  const { height: taH } = useComponentSize(taRef);
  const { height: sH } = useComponentSize(sRef);
  const styles = useSpring({ height: (focused || value.length) ? (taH + sH) : dftH });

  const reset = React.useCallback(() => {
    setFocused(false);
    setValue('');
  }, []);

  return (
    <Form
      style={styles}
      className="wire-remote has-validation-callback"
      id="new_comment"
      action={`/wires/${wireSlug}/comments`}
      acceptCharset="UTF-8"
      method="post"
    >
      <input
        type="hidden"
        name="authenticity_token"
        value={window.Livewire.formAuthenticityToken}
      />
      <textarea
        id="comment_body"
        name="comment[body]"
        ref={taRef}
        value={value}
        onChange={evt => setValue(evt.target.value)}
        onFocus={evt => evt.currentTarget === evt.target && setFocused(true)}
        onBlur={evt => evt.currentTarget === evt.target && setFocused(false)}
        placeholder="Write your comment..."
      />
      <section ref={sRef}>
        <Button name="cancel" type="button" onClick={reset} className="no-focus-outline">cancel</Button>
        <Button name="button" type="submit" disabled={!value.length} className="no-focus-outline">submit</Button>
      </section>
    </Form>
  );
}

CommentForm.propTypes = { wireSlug: PropTypes.string.isRequired };
